import Link from "next/link"
import Image from "next/image"
import { ArrowRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import type { Product } from "@/lib/types"

const FALLBACK_IMAGE = "/images/remote/Adis95-1024x683.jpg"

interface ProductCardProps {
  product: Product
  showCategoryTag?: boolean
  showBrandTag?: boolean
  className?: string
}

export function ProductCard({
  product,
  showCategoryTag = true,
  showBrandTag = true,
  className,
}: ProductCardProps) {
  return (
    <Link
      href={`/produit/${product.slug}`}
      className={cn(
        "group flex h-full rounded-[2rem] p-1.5",
        "bg-foreground/[0.02] ring-1 ring-foreground/5",
        "dark:bg-background/[0.06] dark:ring-foreground/10",
        "transition-[box-shadow,ring-color,transform] duration-300 ease-[var(--ease-premium)]",
        "hover:shadow-[var(--shadow-md)] hover:ring-foreground/10",
        "active:scale-[0.98]",
        className
      )}
    >
      <div className="flex flex-col w-full overflow-hidden rounded-[calc(2rem-0.375rem)] bg-card">
        {/* Image */}
        <div className="relative h-48 overflow-hidden bg-white dark:bg-white/95 rounded-t-[calc(2rem-0.375rem)]">
          <Image
            src={product.imageUrl || FALLBACK_IMAGE}
            alt={product.name}
            fill
            className="object-contain p-5 transition-transform duration-300 ease-[var(--ease-premium)] group-hover:scale-[1.03]"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col p-5">
          {/* Tags */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {showCategoryTag && (
              <Badge variant="secondary" className="text-[10px]">
                {product.category}
              </Badge>
            )}
            {showBrandTag && (
              <Badge variant="outline" className="text-[10px]">
                {product.brand}
              </Badge>
            )}
          </div>

          {/* Title */}
          <h3 className="text-sm font-semibold leading-snug line-clamp-2">
            {product.name}
          </h3>

          {/* Description */}
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground line-clamp-2">
            {product.description}
          </p>

          {/* Link */}
          <div className="mt-auto pt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-primary transition-colors group-hover:text-primary/80">
              Voir le produit
            </span>
            <ArrowRight className="size-3.5 text-primary opacity-0 transition-[transform,opacity] duration-200 group-hover:translate-x-0.5 group-hover:opacity-100" />
          </div>
        </div>
      </div>
    </Link>
  )
}
